import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { getUserInfo } from './apiUtils.js';
import { ChevronDown, Search, SortAsc, SortDesc, Calendar, Filter, X, Plus, AlertTriangle } from 'lucide-react';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');
  const isEmptySystems = message.startsWith('No Integration Systems found');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={{
        ...DialogStyles.dialog,
        borderRadius: isEmptySystems ? '16px' : '8px',
        border: isEmptySystems ? '1px solid #ccc' : 'none',
      }}>
        <h2 style={{
          ...DialogStyles.title,
          fontSize: isEmptySystems ? '16.8px' : '18px',
          lineHeight: isEmptySystems ? '1.4' : '1.2',
        }}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...DialogStyles.button,
              backgroundColor: isEmptySystems ? '#FFFDD0' : '#FFFDD0',
              width: isEmptySystems ? '120px' : '100%',
              margin: isEmptySystems ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

const CreateIntegrationSystemDialog = ({ isOpen, onClose, onSubmit, corporateAccount, projectId, isEditMode, systemToEdit }) => {
  const [systemName, setSystemName] = useState('');
  const [systemDescription, setSystemDescription] = useState('');
  const [systemAcronym, setSystemAcronym] = useState('');
  const [error, setError] = useState('');
  const systemNameInputRef = useRef(null);

  useEffect(() => {
    if (isEditMode && systemToEdit) {
      setSystemName(systemToEdit.system_name);
      setSystemDescription(systemToEdit.system_description);
      setSystemAcronym(systemToEdit.system_acronym);
    } else {
      setSystemName('');
      setSystemDescription('');
      setSystemAcronym('');
    }
  }, [isEditMode, systemToEdit]);



useEffect(() => {
  if (isOpen && !isEditMode) {
    setSystemName('');
    setSystemDescription('');
    setSystemAcronym('');
    setError('');
  }
  else
  if (isEditMode)
    {
    setError('');
    }
}, [isOpen, isEditMode]);




 // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && systemNameInputRef.current) {
      systemNameInputRef.current.focus();
    }
  }, [isOpen]);







  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const endpoint = isEditMode ?
      appConfig.getUrl('/update_integration_system' ):
      appConfig.getUrl('/create_integration_system');

    const requestBody = {
      corporate_account: corporateAccount,
      project_id: projectId,
      system_name: systemName,
      system_description: systemDescription,
      system_acronym: systemAcronym,
      ...(isEditMode && { system_id: systemToEdit.system_id })
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(requestBody)
      });

if (!response.ok) {
  // Try to get error details from JSON response
  let errorMessage = `Failed to fetch systems: ${response.status} ${response.statusText}`;
  try {
    const errorData = await response.json();
    errorMessage = errorData.status_description || errorData.message || errorMessage;
  } catch (parseError) {
    // Use the default message if JSON parsing fails
  }
  throw new Error(errorMessage);
}

      const data = await response.json();

if (data.status === 'Success') {
  setError(data.status_description || 'Integration system saved successfully');

  // Delay the refresh to allow user to see the success message
  setTimeout(() => {
    onSubmit(); // Refresh the systems list

    if (isEditMode) {
      // Close dialog after successful edit
      setTimeout(() => onClose(), 2000);
    } else {
      // Clear form for new entry in create mode
      setSystemName('');
      setSystemDescription('');
      setSystemAcronym('');
      systemNameInputRef.current?.focus();
    }
  }, 2000);
} else {
  throw new Error(data.status_description || 'Failed to save integration system');
}
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(err.message || 'An unexpected error occurred');
    }
};








  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={{...DialogStyles.dialog, width: '500px'}}>
        <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' }}>
          {isEditMode ? 'Edit Integration System' : 'Create New Integration System'}
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>System Name*:</label>
            <input
              type="text"
              value={systemName}
              ref={systemNameInputRef}
              onChange={(e) => setSystemName(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              required
            />
          </div>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>Description*:</label>
            <input
              type="text"
              value={systemDescription}
              onChange={(e) => setSystemDescription(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              required
            />
          </div>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>Acronym*:</label>
            <input
              type="text"
              value={systemAcronym}
              onChange={(e) => setSystemAcronym(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              required
            />
          </div>
                        {error && (
                          <div className={`mb-4 p-2 rounded ${
                            error.includes('successfully')
                              ? 'bg-green-50 text-green-700'
                              : 'bg-red-50 text-red-500'
                          }`}>
                            {error}
                          </div>
                        )}
         <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={onClose}
              style={{
                    backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {isEditMode ? 'Update' : 'Add New System'}
            </button>





          </div>

                                {!isEditMode && (
            <p className="text-gray-600 text-sm mt-4 text-center">
              You can continue adding more systems. Click 'Close' when finished.
            </p>
          )}


        </form>
      </div>
    </div>
  );
};




const IntegrationSystems = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [redirectPath, setRedirectPath] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSystemForEdit, setSelectedSystemForEdit] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSystems, setFilteredSystems] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [permissions, setPermissions] = useState({
    canUpdate: false,
    canDelete: false
  });

  const login_token = localStorage.getItem('authToken');
  const [systems, setSystems] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [corporateAccount, setCorporateAccount] = useState('');
  const [currentProject, setCurrentProject] = useState('');
  const [userId, setUserId] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [userName, setUserName] = useState('');

const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
const [itemToDelete, setItemToDelete] = useState(null);

  // State for pagination
  const [itemsPerPage, setItemsPerPage] = useState(10);

// Sorting functionality
const handleSort = (key) => {
  setSortConfig(prevConfig => ({
    key,
    direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
  }));
};

// Apply sorting to systems
const sortedSystems = React.useMemo(() => {
  let sortableSystems = [...filteredSystems];
  if (sortConfig.key) {
    sortableSystems.sort((a, b) => {
      let aVal = a[sortConfig.key] || '';
      let bVal = b[sortConfig.key] || '';

      // Convert to string for consistent comparison
      aVal = aVal.toString().toLowerCase();
      bVal = bVal.toString().toLowerCase();

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }
  return sortableSystems;
}, [filteredSystems, sortConfig]);


  const styles = {
    deleteButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    editButton: {
      backgroundColor: '#4CAF50',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    disabled: {
      opacity: 0.6
    },
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '0px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      fontSize: '0.875rem',
    },
th: {
  backgroundColor: '#E5E4E2',
  color: '#0064e5',
  padding: '6px 12px',
  textAlign: 'left',
  borderBottom: '2px solid #ddd',
  fontSize: '0.875rem',
  lineHeight: '1',
  height: '32px',
  cursor: 'pointer', // Add this to indicate it's clickable
  userSelect: 'none', // Prevent text selection when clicking
  position: 'relative' // For positioning the sort icons
},
thNonSortable: {
  backgroundColor: '#E5E4E2',
  color: '#0064e5',
  padding: '6px 12px',
  textAlign: 'left',
  borderBottom: '2px solid #ddd',
  fontSize: '0.875rem',
  lineHeight: '1',
  height: '32px'
},
    td: {
      padding: '12px',
      borderBottom: '1px solid #ddd',
    },
    checkbox: {
      marginRight: '10px',
    },
    button: {
      padding: '6px 12px',
      margin: '0 5px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    error: {
      color: 'red',
      padding: '10px',
      marginBottom: '10px',
    },
  };



const renderDeleteConfirmDialog = () => {
  if (!isDeleteConfirmOpen || !itemToDelete) return null;

  return (
    <div style={{...DialogStyles.overlay, zIndex: 1100}}>
      <div style={{...DialogStyles.dialog, width: '600px'}}>
        <div style={{
          backgroundColor: '#FFE6E6',
          color: '#CC0000',
          padding: '15px 0',
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: 'bold',
          borderRadius: '8px 8px 0 0',
          margin: '-20px -20px 20px -20px'
        }}>
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle size={20} />
            <span>Confirm Deletion</span>
          </div>
        </div>

        <div className="px-6 py-4">
          <p className="text-gray-700 mb-4">
            {itemToDelete.id === 'multiple'
              ? `Are you sure you want to delete these integration systems?`
              : `Are you sure you want to delete this integration system?`}
          </p>
          <p className="text-sm text-red-600 mb-4">
            This action cannot be undone.
          </p>

          {/* Show system details */}
          <div className="bg-gray-50 rounded p-3 mb-4">
            <p className="text-xs text-gray-600 mb-2">System details:</p>
            <div className="text-sm text-gray-700">
              {itemToDelete.id === 'multiple' ? (
                <div><strong>Selected Systems:</strong> {itemToDelete.description}</div>
              ) : (
                <>
                  <div><strong>System Name:</strong> {itemToDelete.system_name}</div>
                  <div><strong>Description:</strong> {itemToDelete.system_description || 'No description'}</div>
                  <div><strong>Acronym:</strong> {itemToDelete.system_acronym || 'No acronym'}</div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4 p-6 pt-0">
          <button
            onClick={() => {
              setIsDeleteConfirmOpen(false);
              setItemToDelete(null);
            }}
            style={{
              backgroundColor: '#f8f9fa',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirmed}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {itemToDelete.id === 'multiple'
              ? 'Delete Integration Systems'
              : 'Delete Integration System'}
          </button>
        </div>
      </div>
    </div>
  );
};

  const checkPermission = async (categoryHeader) => {
  try {
    const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        user_id: userId,
        project_id: currentProject,
        category_header: categoryHeader,
        category_sub_header: []
      }),
    });
    const data = await response.json();
    // Create new permissions object
    const newPermissions = { canUpdate: false , canDelete: false };
    // Map API response to permission fields
    data.user_actions_list.forEach(item => {
      switch(item.user_action) {
        case 'Create Or Edit Integration System':
          newPermissions.canUpdate = item.has_access;
          break;
        case 'Delete Integration System':
          newPermissions.canDelete = item.has_access;
          break;
      }
    });
    setPermissions(newPermissions);
return data.access_status && data.status === 'Success';
  } catch (error) {
    console.error(`Error checking permission for ${categoryHeader}:`, error);
    return false;
  }
};






useEffect(() => {
  if (corporateAccount && currentProject) {
    // Check permissions and fetch systems only if the user has access
    console.log('fetching systems...#1');
    fetchSystems();
    checkPermission('Define Integration Systems');
  }
}, [corporateAccount, currentProject] );



  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setRedirectPath(path);
    setShowDialog(true);
  };

 const handleDialogClose = () => {
  setShowDialog(false);
  if (redirectPath) {
    window.location.href = redirectPath;
  }
  // Remove the pendingDeleteAction logic since we're not using it anymore
};


const handleDialogCancel = () => {
  setShowDialog(false);
  // Remove the pendingDeleteAction logic
};

  const handleCreateSystem = () => {
      setSelectedRows(new Set());  // Add this line
    console.log('fetching systems...#2');

    fetchSystems();
  };

  const handlePageChange = (newPage) => {
    setSelectedRows(new Set());
    setCurrentPage(newPage);
};


  const handleSearch = () => {
    console.log('fetching systems...#3');

    fetchSystems();
  };


const handleRowEdit = (system) => {
  if (permissions.canUpdate) {
    setSelectedSystemForEdit(system);
    setIsEditMode(true);
    setIsCreateDialogOpen(true);
  }
};

  // In the handleDelete function, replace the existing success handling with this:

const handleDelete = async () => {
  if (!permissions.canDelete) {
    setError('Insufficient permissions to delete items');
    return;
  }

  if (selectedRows.size === 0) {
    setError('Please select at least one system to delete');
    return;
  }

  // For single selection, show detailed confirmation
  if (selectedRows.size === 1) {
    const selectedId = Array.from(selectedRows)[0];
    const system = systems.find(s => s.system_id === selectedId);

    setItemToDelete({
      id: selectedId,
      system_name: system?.system_name,
      system_description: system?.system_description,
      system_acronym: system?.system_acronym
    });

    setIsDeleteConfirmOpen(true);
    return;
  }

  // For multiple selections, create a summary object
  const multipleItemsObject = {
    id: 'multiple',
    description: `${selectedRows.size} selected system${selectedRows.size > 1 ? 's' : ''}`,
    actualIds: Array.from(selectedRows)
  };

  setItemToDelete(multipleItemsObject);
  setIsDeleteConfirmOpen(true);
};

const handleDeleteConfirmed = async () => {
  setIsDeleteConfirmOpen(false);
  setIsDeleting(true);
  setDeleteError(null);
  setError(null);

  try {
    // Handle both single and multiple deletions
    const systemIds = itemToDelete.id === 'multiple'
      ? itemToDelete.actualIds
      : [itemToDelete.id];

    const response = await fetch(appConfig.getUrl('/delete_integration_system'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        system_ids: systemIds
      }),
    });

    const data = await response.json();

    if (data.status === 'Success') {
      const message = systemIds.length === 1
        ? 'Integration system deleted successfully!'
        : 'Integration systems deleted successfully!';

      // Set success message in the error state (which handles both success and error styling)
      setError(data.status_description || message);

      // Clear selections and refresh after showing success message
      setTimeout(() => {
        setSelectedRows(new Set());
        console.log('fetching systems...#4');
        fetchSystems();

        // Clear the success message after refresh
        setTimeout(() => {
          setError(null);
        }, 2000);
      }, 2000);

    } else {
      throw new Error(data.status_description || 'Failed to delete systems');
    }
  } catch (err) {
    setDeleteError(err.message);
    setError('Failed to delete systems: ' + err.message);
  } finally {
    setIsDeleting(false);
    setItemToDelete(null);
  }
};


//  useEffect(() => {
//    if (corporateAccount) {
//        console.log('fetching systems...#5');
//
//      fetchSystems();
//    }
//  }, [corporateAccount, currentProject]);

  useEffect(() => {
    if (searchQuery === '' && corporateAccount && currentProject) {
        console.log('Call #6: fetchSystems');
      fetchSystems();
    }
  }, [searchQuery]);

  useEffect(() => {
    setFilteredSystems(systems);
  }, [systems]);

  useEffect(() => {
    const setInitialLoad = async () => {
      const current_project = localStorage.getItem('current_project');
      if (!login_token) {
        showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
        return;
      }
      try {
        const result = await getUserInfo({
          login_token: login_token
        });

        if (result.success) {
          if (result.userInfo.default_project == 'null' && current_project == 'null') {
            showErrorDialog('Please select a project on the home page', '/RequirementsPortal');
            return;
          }
          setCorporateAccount(result.userInfo.corporate_account);
          setUserId(result.user_id);
          setUserName(result.userInfo.user_name);
          setAccountDescription(result.userInfo.account_description);
          setCurrentProject(current_project === 'null' ? result.userInfo.default_project : current_project);
        } else {
          showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
        }
      } catch (error) {
        console.error('Error calling API:', error);
      }
    };

    setInitialLoad();
  }, []);










  const fetchSystems = async () => {


  setLoading(true);
  setSelectedRows(new Set());
setCurrentPage(1); // Reset to first page on new fetch

  try {
    const requestBody = {
      corporate_account: corporateAccount,
      project_id: currentProject,
      search_query: searchQuery,
      filter_by_status: ["Active", "Inactive"],
      sort_criteria: "system_name"
    };

    const response = await fetch(appConfig.getUrl('/get_integration_systems_list'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify(requestBody),
    });


    if (!response.ok) {
      throw new Error(`Failed to fetch systems: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();


    if (data.status === 'Success') {
      setSystems(data.integration_systems_list || []);

      // Add a timeout to check if state was actually updated
      setTimeout(() => {
        console.log('Systems state after setSystems:', systems);
      }, 100);

    } else {
      setSystems([]);
      if (data.integration_systems_list?.length === 0) {
//        setDialogMessage('No Integration Systems found to load.\nCreate new or copy from predefined list');
//        setShowDialog(true);
      } else {
        throw new Error(data.status_description || 'Failed to fetch systems');
      }
    }
  } catch (err) {
    console.error('=== FETCH ERROR ===');
    console.error('Error fetching systems:', err);
    console.error('Error message:', err.message);
    console.error('Error stack:', err.stack);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};















  const toggleSelectAll = () => {
    if (selectedRows.size === paginatedSystems.length) {
      setSelectedRows(new Set());
    } else {
      const newSelected = new Set(paginatedSystems.map(system => system.system_id));
      setSelectedRows(newSelected);
    }
  };

  const toggleRowSelection = (systemId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(systemId)) {
      newSelected.delete(systemId);
    } else {
      newSelected.add(systemId);
    }
    setSelectedRows(newSelected);
  };

// This is what you need (make sure both lines use the defensive checks):
const totalPages = Math.ceil((sortedSystems?.length || 0) / itemsPerPage);
const startIndex = (currentPage - 1) * itemsPerPage;
const paginatedSystems = (sortedSystems || []).slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <div>Loading...</div>;
//  if (error) return <div style={styles.error}>{error}</div>;

  const mainStyles = {
    headerHeight: '64px',
    sectionHeaderHeight: '48px'
  };


  return (


    <div className="min-h-screen bg-gray-50">
      <Header default_project={currentProject} />

      <div className="flex h-[calc(100vh-64px)]">
        <div className="flex-1 overflow-y-auto p-6">
          <div style={styles.container}>









 {/* Title bar */}
<h1 className="text-l font-bold text-blue-800 mt-6 mr-20 text-center">Manage Integration Systems</h1>
  <div className="flex items-center mb-4 mt-3 justify-between bg-blue-100 p-3 rounded">

    {/* Search bar */}
    <div className="flex items-center">
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSearch();
          }
        }}
        placeholder="Search systems..."
        className="p-2 pr-10 border rounded w-full"
      />
      <button
        onClick={handleSearch}
        className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
      >
        <Search size={18} />
      </button>
    </div>

    <h2 className="text-l font-bold text-blue-700 text-center flex-1">{currentProject}</h2>

<button
  disabled={!permissions.canUpdate}
  onClick={() => {
    if (permissions.canUpdate) {
      setIsEditMode(false);
      setSelectedSystemForEdit(null);
      setIsCreateDialogOpen(true);
    }
  }}
  className={`group relative flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${!permissions.canUpdate ? 'opacity-50' : ''}`}
>
  <Plus size={18} className="mr-2" />
  Create New

  {/* Hover tooltip for disabled state */}
  {!permissions.canUpdate && (
        <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
      Insufficient permission
      <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-blue-600"></div>
    </div>
  )}
</button>




  </div>

 {/* End Title bar */}










            <table style={styles.table}>
                 <thead>
                  <tr>
                    <th style={styles.thNonSortable}>
                      <input
                        type="checkbox"
                        checked={paginatedSystems.length > 0 && selectedRows.size === paginatedSystems.length}
                        onChange={toggleSelectAll}
                        style={styles.checkbox}
                      />
                      Select All
                    </th>
                    <th
                      style={styles.th}
                      onClick={() => handleSort('system_name')}
                      className="select-none hover:bg-gray-200 transition-colors"
                    >
                      <div className="flex items-center">
                        <span style={{ textDecoration: 'underline' }}>System Name</span>
                        <span className="ml-1">
                          {sortConfig.key === 'system_name' ? (
                            sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                          ) : null}
                        </span>
                      </div>
                    </th>
                    <th
                      style={styles.th}
                      onClick={() => handleSort('system_description')}
                      className="select-none hover:bg-gray-200 transition-colors"
                    >
                      <div className="flex items-center">
                        <span style={{ textDecoration: 'underline' }}>Description</span>
                        <span className="ml-1">
                          {sortConfig.key === 'system_description' ? (
                            sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                          ) : null}
                        </span>
                      </div>
                    </th>
                    <th
                      style={styles.th}
                      onClick={() => handleSort('system_acronym')}
                      className="select-none hover:bg-gray-200 transition-colors"
                    >
                      <div className="flex items-center">
                        <span style={{ textDecoration: 'underline' }}>Acronym</span>
                        <span className="ml-1">
                          {sortConfig.key === 'system_acronym' ? (
                            sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                          ) : null}
                        </span>
                      </div>
                    </th>
                    <th style={styles.thNonSortable}>
                      Actions
                    </th>
                  </tr>
                </thead>



                <tbody>
                  {paginatedSystems.map(system => (
                    <tr key={system.system_id}
                        style={{
                          backgroundColor: selectedRows.has(system.system_id) ? '#DEEFF5' : 'transparent'
                        }}
                        className="border-t hover:bg-gray-50"
                    >
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={selectedRows.has(system.system_id)}
                          onChange={() => toggleRowSelection(system.system_id)}
                          style={styles.checkbox}
                        />
                      </td>
                      <td style={styles.td}>{system.system_name}</td>
                      <td style={styles.td}>{system.system_description}</td>
                      <td style={styles.td}>{system.system_acronym}</td>
                      {/* NEW: Added Actions column with edit icon */}
                      <td style={styles.td}>
                        <div className="group relative">
                          <button
                            onClick={() => handleRowEdit(system)}
                            disabled={!permissions.canUpdate}
                            className={`p-1 rounded hover:bg-blue-100 ${
                              !permissions.canUpdate ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            style={{ color: permissions.canUpdate ? '#4CAF50' : '#999' }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.828-2.828z" />
                            </svg>
                          </button>

                          {/* Edit button tooltip */}
                          {!permissions.canUpdate && (
                            <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                              Insufficient permission
                              <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {sortedSystems.length === 0 && (
                    <tr>
                      {/* UPDATED: Changed colspan from 4 to 5 */}
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        No systems found. Create a new system or adjust your search.
                      </td>
                    </tr>
                  )}
                </tbody>


            </table>


                     {/* Error/Success Messages */}
                      {error && (
                        <div className={`mb-4 p-2 rounded ${
                          error.toLowerCase().includes('successfully')
                            ? 'bg-green-50 text-green-700'
                            : 'bg-red-50 text-red-500'
                        }`}>
                          {error}
                        </div>
                      )}






 {/* Pagination controls */}
{!loading && sortedSystems.length > 0 && (
  <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
    <div className="flex items-center gap-2 text-sm text-gray-600">
<span>Total Entries: {sortedSystems.length}</span>
<span>| Showing {sortedSystems.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedSystems.length)} of {sortedSystems.length}</span>
    </div>

    <div className="flex items-center gap-2">
      <button
        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
      <button
        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages || totalPages === 0}
        aria-label="Next page"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <div className="flex items-center">
      <span className="text-sm text-gray-600 mr-2">Items per page:</span>
      <select
        value={itemsPerPage}
        onChange={(e) => {
          setItemsPerPage(Number(e.target.value));
          setCurrentPage(1); // Reset to first page when changing items per page
        }}
        className="border rounded p-1 text-sm"
      >
        <option value={5}>5</option>
        <option value={10}>10</option>
        <option value={20}>20</option>
        <option value={50}>50</option>
      </select>
    </div>
  </div>
)}
 {/* End Pagination controls */}





       <div style={{
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '20px',
  width: '100%'
}}>
  <div style={{ display: 'flex', gap: '10px' }}>
    {/* Delete button with tooltip */}
    <div className="group relative">
      <button
        style={{
          ...styles.deleteButton,
          padding: '8px 16px',
          ...(isDeleting || selectedRows.size === 0 || !permissions.canDelete ? styles.disabled : {})
        }}
        onClick={handleDelete}
        disabled={isDeleting || selectedRows.size === 0 || !permissions.canDelete}
      >
        {isDeleting ? 'Deleting...' : `Delete Selected (${selectedRows.size})`}
      </button>

      {/* Delete button tooltip */}
      {(selectedRows.size === 0 || !permissions.canDelete) && (
        <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
          {selectedRows.size === 0
            ? "Select one or more items to delete"
            : "Insufficient permission"
          }
          <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
        </div>
      )}
    </div>


  </div>

  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  }}>

  </div>
</div>







          </div>
        </div>


      </div>

{showDialog && (
  <Dialog
    message={dialogMessage}
    onClose={handleDialogClose}
    onCancel={handleDialogCancel}
  />
)}

{/* Delete Confirmation Dialog */}
{renderDeleteConfirmDialog()}

      <CreateIntegrationSystemDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setIsEditMode(false);
          setSelectedSystemForEdit(null);
        }}
        onSubmit={handleCreateSystem}
        corporateAccount={corporateAccount}
        projectId={currentProject}
        isEditMode={isEditMode}
        systemToEdit={selectedSystemForEdit}
      />
    </div>
  );


};

export default IntegrationSystems;